import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '@/shared/lib/utils';
import { PopoverContent } from '@/shared/ui/Popover/components/PopoverContent';
import { PopoverRoot } from '@/shared/ui/Popover/components/PopoverRoot';
import { PopoverTrigger } from '@/shared/ui/Popover/components/PopoverTrigger';
import { Tag } from '@/shared/ui/tag/Tag';
import { formFieldSizeVariants } from '../helpers/field-size';
import { selectTriggerStyles } from '../select/helpers/trigger-styles';
import type { CascaderOption, CascaderProps } from './helpers';
import { getTagLabel } from './utils/display-utils';

type SearchableMultipleCascaderProps = {
  id?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  disabled: boolean;
  hasError: boolean;
  className?: string;
  size: CascaderProps['size'];
  shouldShowClearButton: boolean;
  onClear: (e: React.MouseEvent<HTMLButtonElement>) => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  onSearchKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  placeholder: string;
  listboxId?: string;
  activeOptionId?: string;
  visibleTags: string[][];
  remainingCount: number;
  processedOptions: CascaderOption[];
  selectedPaths: string[][];
  onRemoveTag: (path: string[]) => void;
  popoverContent: React.ReactNode;
};

export const SearchableMultipleCascader = ({
  id,
  open,
  onOpenChange,
  disabled,
  hasError,
  className,
  size = 'sm',
  shouldShowClearButton,
  onClear,
  inputRef,
  searchValue,
  onSearchValueChange,
  onSearchKeyDown,
  placeholder,
  listboxId,
  activeOptionId,
  visibleTags,
  remainingCount,
  processedOptions,
  selectedPaths,
  onRemoveTag,
  popoverContent,
}: SearchableMultipleCascaderProps) => {
  return (
    <div className={cn('relative w-full', className)}>
      <PopoverRoot open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger asChild disabled={disabled}>
          {/* biome-ignore lint/a11y/noStaticElementInteractions: クリックで内部 input にフォーカスを委譲するラッパー */}
          {/* biome-ignore lint/a11y/useKeyWithClickEvents: キーボード操作は内部 input の onKeyDown で処理 */}
          <div
            onClick={(e) => {
              if (disabled) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              inputRef.current?.focus();
            }}
            className={cn(
              selectedPaths.length === 0
                ? selectTriggerStyles({ height: 'fixed' })
                : selectTriggerStyles({ height: 'flexible' }),
              disabled ? 'cursor-not-allowed bg-surface-strength1' : 'cursor-pointer',
              formFieldSizeVariants({ size }),
              shouldShowClearButton && 'pr-10',
            )}
          >
            <div className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
              {selectedPaths.length > 0 ? (
                <>
                  {visibleTags.map((path) => {
                    const label = getTagLabel(path, processedOptions);
                    return (
                      <Tag
                        style={{ maxWidth: '200px' }}
                        key={JSON.stringify(path)}
                        onRemove={() => onRemoveTag(path)}
                      >
                        {label}
                      </Tag>
                    );
                  })}
                  {remainingCount > 0 && (
                    <span className="inline-flex items-center rounded bg-secondary px-2 py-0.5 text-xs">
                      +{remainingCount}
                    </span>
                  )}
                </>
              ) : null}
              <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listboxId}
                aria-activedescendant={activeOptionId}
                aria-invalid={hasError || undefined}
                disabled={disabled}
                id={id}
                autoComplete="off"
                value={searchValue}
                placeholder={selectedPaths.length === 0 ? placeholder : '検索...'}
                onChange={(e) => {
                  onSearchValueChange(e.target.value);
                  if (!open) onOpenChange(true);
                }}
                onKeyDown={onSearchKeyDown}
                className={cn(
                  'border-none bg-transparent text-sm shadow-none outline-none',
                  'p-0',
                  'placeholder:text-text-muted',
                  'focus:outline-none focus:ring-0',
                )}
              />
            </div>
            {selectedPaths.length === 0 && (
              <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={4}
          className="flex w-auto flex-col bg-surface-base p-2"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onFocusOutside={(event) => event.preventDefault()}
        >
          {popoverContent}
        </PopoverContent>
      </PopoverRoot>
      {shouldShowClearButton && (
        <button
          type="button"
          onClick={onClear}
          className="absolute top-1/2 right-3 -translate-y-1/2 text-text-muted transition-colors hover:text-foreground"
          aria-label="選択をクリア"
          tabIndex={-1}
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
};
